<template>
  <div>
    <div class="i-mb-14">
      <settings-section-title id="details" class="i-mb-2"
        >Account</settings-section-title
      >
      <p class="i-text-body-2 i-text-neutral-110">
        View and update your account details
      </p>
    </div>
    <div class="settings-page-block i-py-14 i-section-divide">
      <div class="settings-page-block-title">
        <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">Details</h5>
        <p class="i-text-body-2 i-text-neutral-110">
          Update your account details
        </p>
      </div>
      <div class="settings-page-block-content">
        <label class="i-text-subheading-2 i-text-neutral-120">Email:</label>
        <i-copy
          data-testid="email-display"
          element-id="email-display"
          class="i-mt-3"
          :model-value="currentUser.email"
        ></i-copy>
      </div>
    </div>
    <div class="settings-page-block i-py-14 i-section-divide">
      <div class="settings-page-block-title">
        <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">Password</h5>
        <p class="i-text-body-2 i-text-neutral-110">
          Confirm your current password before <br />
          setting a new one. 10 characters minimum.
        </p>
      </div>
      <div class="settings-page-block-content">
        <div class="form-group i-mb-1">
          <form class="d-flex flex-column i-gap-8" @submit.prevent="submit()">
            <i-text-input
              id="old-password"
              :model-value="oldPassword"
              label="Current password"
              placeholder="Current password"
              autocomplete="current-password"
              type="password"
              :has-error="
                displayPasswordHint &&
                (!oldPassword || oldPassword.length === 0)
              "
              :show-hint-text="displayPasswordHint && !oldPassword"
              hint-text="Current password must not be blank"
              @update:model-value="oldPassword = $event"
            >
            </i-text-input>

            <password-input
              id="new-password"
              :model-value="newPassword"
              :display-hint="displayPasswordHint"
              label="New password"
              type="password"
              autocomplete="new-password"
              placeholder="New Password"
              @update:model-value="newPassword = $event"
            />

            <i-text-input
              id="confirm-password"
              :model-value="confirmationPassword"
              label="Confirm password"
              placeholder="Password confirmation"
              autocomplete="new-password"
              name="password_confirmation"
              type="password"
              :has-error="displayPasswordHint && !doPasswordsMatch"
              :show-hint-text="displayPasswordHint && !doPasswordsMatch"
              hint-text="Your passwords don't match"
              @update:model-value="confirmationPassword = $event"
            >
            </i-text-input>

            <div class="i-mt-4">
              <i-button
                text="Submit"
                size="lg"
                data-testid="save-password-changes-button"
              ></i-button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div class="settings-page-block i-py-14 i-section-divide">
      <div class="settings-page-block-title">
        <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">Security</h5>
        <p class="i-text-body-2 i-text-neutral-110">
          Require a verification code in addition to your password
        </p>
      </div>
      <div class="settings-page-block-content">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex flex-column i-gap-3">
            <div class="d-flex align-items-center i-gap-2">
              <label class="i-m-0">Two Factor Authentication:</label>
              <info-icon
                title="Two Factor Authentication"
                description="Two factor auth (2FA) adds an extra level of protection to your account by requiring you to verify your login with a code or a push notification on your phone"
              ></info-icon>
            </div>
            <b>{{ twoFaEnabled ? 'Enabled' : 'Disabled' }}</b>
          </div>
          <div>
            <i-button
              :text="twoFaEnabled ? 'Disable' : 'Enable'"
              icon
              icon-trailing
              :type="twoFaEnabled ? 'error' : 'primary'"
              :subtype="twoFaEnabled ? 'subtle' : 'strong'"
              :icon-symbol="twoFaEnabled ? 'small-unlock' : 'small-lock'"
              :disabled="isLoadingDisableTwoFa"
              data-testid="enable-disable-two-fa-button"
              @button-click="toggleTwoFa"
            ></i-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import InfoIcon from '@/V2/components/InfoIcon';
import { iCopyInput, iButton, iTextInput, showToast } from '@inscribe/signal';
import PasswordInput from '@/V2/auth/PasswordInput';
import { isValidPassword } from '@/utils/validation';
import SettingsSectionTitle from './shared/SettingsSectionTitle';

export default {
  components: {
    'i-button': iButton,
    'i-copy': iCopyInput,
    iTextInput,
    InfoIcon,
    'password-input': PasswordInput,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      oldPassword: '',
      newPassword: '',
      confirmationPassword: '',
      loadingPasswordChange: false,
      isLoadingDisableTwoFa: false,
      displayPasswordHint: false,
    };
  },
  computed: {
    ...mapState(['currentUser']),

    twoFaEnabled() {
      return this.currentUser.two_fa_enabled;
    },
    isValidPassword() {
      return isValidPassword(this.confirmationPassword);
    },
    doPasswordsMatch() {
      return this.newPassword === this.confirmationPassword;
    },
    passwordsChangeIsValid() {
      return this.oldPassword && this.isValidPassword && this.doPasswordsMatch;
    },
  },
  methods: {
    toggleTwoFa() {
      const message =
        'Are you sure you want to disable two factor authentication? This will make your account less secure.';
      if (!this.twoFaEnabled) {
        this.$router.push({
          name: 'enable-2fa',
        });
      } else if (confirm(message)) {
        this.isLoadingDisableTwoFa = true;
        this.$store
          .dispatch('disableTwoFa')
          .then((response) => {
            this.isLoadingDisableTwoFa = false;
            this.$store.dispatch('getCurrentUser');
            showToast({
              text: response?.data?.message,
            });
          })
          .catch((error) => {
            this.isLoadingDisableTwoFa = false;
            showToast({
              text: `Could not disable two factor authentication, error: ${error?.data?.message}.`,
              isError: true,
            });
          });
      }
    },
    submit() {
      this.loadingPasswordChange = true;
      this.displayPasswordHint = false;
      if (
        !this.oldPassword ||
        !this.newPassword ||
        !this.confirmationPassword
      ) {
        this.displayPasswordHint = true;
      } else {
        this.$store
          .dispatch('changePassword', {
            oldPassword: this.oldPassword,
            newPassword: this.newPassword,
            confirmationPassword: this.confirmationPassword,
          })
          .then((response) => {
            this.oldPassword = '';
            this.newPassword = '';
            this.confirmationPassword = '';
            this.loadingPasswordChange = false;

            if (response.data.success) {
              showToast({
                text: response?.data?.message,
              });
            } else {
              this.loadingPasswordChange = false;
              showToast({
                text: `Could not change password, error: ${response?.data?.message}.`,
                isError: true,
              });
            }
          });
      }
    },
  },
};
</script>
